import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { SearchEntityType } from '@/entities/search';
import { useSearchQuery } from '@/entities/search';
import { useSmartSearchState } from './useSmartSearchState';
import { useCommittedSearch } from './useCommittedSearch';
import { usePredicateSync } from './usePredicateSync';
import { buildSearchQueryParams, buildSearchRequest } from './buildSearchRequest';

export type UseSmartSearchPaginatedParams<
  EntityType extends SearchEntityType,
  Filters extends Record<string, unknown> = Record<string, unknown>,
> = {
  entityType: EntityType;
  /** Base filters merged into request */
  baseFilters?: Filters;
  pageSize?: number;
};

/**
 * Smart search + manual pagination (prev/next buttons).
 * Uses resolvedDslRef to cache NL→DSL conversion for pagination (avoids re-conversion on page change).
 */
export const useSmartSearchPaginated = <
  EntityType extends SearchEntityType,
  Item,
  Filters extends Record<string, unknown> = Record<string, unknown>,
>(
  params: UseSmartSearchPaginatedParams<EntityType, Filters>,
) => {
  const { entityType, baseFilters = {} as Filters, pageSize = 50 } = params;

  const searchState = useSmartSearchState();
  const { committed, commit, clear: clearCommitted } = useCommittedSearch();
  const [page, setPage] = useState(0);
  const [isSearchAnimating, setIsSearchAnimating] = useState(false);
  const resolvedDslRef = useRef<string | null>(null);

  const queryParams = useMemo(() => buildSearchQueryParams(committed), [committed]);

  const request = useMemo(() => {
    const base = buildSearchRequest(entityType, queryParams, {
      limit: pageSize,
      offset: page * pageSize,
    });

    // Use cached DSL from NL conversion for pagination
    if (resolvedDslRef.current && committed?.mode === 'nl') {
      delete base.dslHuman;
      base.dsl = resolvedDslRef.current;
    }

    base.filters = { ...(base.filters ?? {}), ...baseFilters };
    return base;
  }, [entityType, queryParams, committed, page, baseFilters, pageSize]);

  const { data, isLoading, isFetching } = useSearchQuery<Item>(request);

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const predicateInfo = data?.predicate ?? null;
  const totalPages = Math.ceil(total / pageSize);

  usePredicateSync({
    predicateInfo,
    committedSearch: committed,
    onSearchChange: searchState.onChange,
    resolvedDslRef,
  });

  useEffect(() => {
    if (!isFetching && isSearchAnimating) {
      setIsSearchAnimating(false);
    }
  }, [isFetching, isSearchAnimating]);

  const handleSearch = useCallback(() => {
    const query = searchState.searchMode === 'nl' ? searchState.value.human.trim() : searchState.value.dsl.trim();
    resolvedDslRef.current = null;
    if (query) setIsSearchAnimating(true);
    commit(query, searchState.searchMode);
    setPage(0);
  }, [searchState.searchMode, searchState.value, commit]);

  const handleClear = useCallback(() => {
    searchState.clearSearch();
    resolvedDslRef.current = null;
    clearCommitted();
    setPage(0);
  }, [searchState, clearCommitted]);

  return {
    searchState,
    items,
    total,
    page,
    setPage,
    totalPages,
    predicateInfo,
    handleSearch,
    handleClear,
    isLoading,
    isSearchAnimating,
  };
};
